import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { DELIVERY_STATUS, DELIVERY_PARTNER_IDS_MAP, NO_TRACKING_REQUIRED_PARTNER_IDS } from "../../../components/delivery/constants";
import { isManualDelivery, getDeliveryTrackingLink } from "../../../components/delivery/helper";

export { formatCurrency } from "../../utils/format";

// Shared primitives for the logistics console
// (`.scratch/logistics-dashboard/IMPLEMENTATION.md`).
//
// This console is a RESTYLE of the legacy /delivery page, not a reimplementation: it reads
// the same endpoint and calls the same action APIs. Everything here is presentation plus the
// one piece of new logic the prototype asked for -- recommendation-first row actions (§5).

export const QUEUE_TABS = [
    { id: "new", label: "New" },
    { id: "error", label: "Error" },
    { id: "delivered", label: "Delivered" },
    { id: "cancelled", label: "Cancelled" },
];

// COD Watch drops the Cancelled tab (product owner, 2026-09-15: "keep only non-cancelled
// deliveries"). A cancelled shipment has no COD left to collect or reconcile, so it is noise
// on this lens.
//
// Removing the tab is the whole filter: the tab is what selects the status list sent to the
// API (DELIVERY_TAB_STATUS_FILTER), so with no Cancelled tab there is no request that can
// return CANCELLED rows. Delivered is kept — COD that was collected still matters.
export const COD_TABS = QUEUE_TABS.filter((tab) => tab.id !== "cancelled");

// §6 -- operator-readable labels. HANDOFF.md is explicit that internal shorthand (NDR, raw
// enums) must not reach this UI. ERROR -> "Delivery Exception" is the one the guidance was
// written about.
const STATUS_LABELS = {
    [DELIVERY_STATUS.PACKAGING]: "Packaging",
    [DELIVERY_STATUS.PACKED]: "Packed",
    [DELIVERY_STATUS.READY_TO_SHIP]: "Ready to Ship",
    [DELIVERY_STATUS.IN_TRANSIT]: "In Transit",
    [DELIVERY_STATUS.OUT_FOR_DELIVERY]: "Out for Delivery",
    [DELIVERY_STATUS.DELIVERED]: "Delivered",
    [DELIVERY_STATUS.CANCELLED]: "Cancelled",
    [DELIVERY_STATUS.RETURN]: "Returning",
    [DELIVERY_STATUS.ERROR]: "Delivery Exception",
};

// `deliveries.status` is a plain STRING column, not a Postgres enum, so an unmapped value is
// possible. Title-case it rather than printing SCREAMING_SNAKE at an operator.
export function statusLabel(status) {
    if (!status) return "—";
    if (STATUS_LABELS[status]) return STATUS_LABELS[status];
    return String(status)
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

const STATUS_TONES = {
    [DELIVERY_STATUS.DELIVERED]: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    [DELIVERY_STATUS.ERROR]: "bg-red-50 text-red-700 ring-red-600/20",
    [DELIVERY_STATUS.CANCELLED]: "bg-gray-100 text-gray-600 ring-gray-500/20",
    [DELIVERY_STATUS.RETURN]: "bg-amber-50 text-amber-700 ring-amber-600/20",
    [DELIVERY_STATUS.OUT_FOR_DELIVERY]: "bg-sky-50 text-sky-700 ring-sky-600/20",
    [DELIVERY_STATUS.IN_TRANSIT]: "bg-sky-50 text-sky-700 ring-sky-600/20",
};
const STATUS_TONE_DEFAULT = "bg-slate-50 text-slate-700 ring-slate-500/20";

export function StatusPill({ status }) {
    const tone = STATUS_TONES[status] || STATUS_TONE_DEFAULT;
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${tone}`}>
            {statusLabel(status)}
        </span>
    );
}
StatusPill.propTypes = { status: PropTypes.string };

export function CodChip({ isCod }) {
    return isCod ? (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
            Yes
        </span>
    ) : (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-500/20">
            No
        </span>
    );
}
CodChip.propTypes = { isCod: PropTypes.bool };

// Name + phone. Both come from userOrder.mapUserPatient, which Delivery.Get attaches from
// the MyTatva user service AFTER the query (classes/Delivery/Delivery.js:150) -- it is not a
// SQL join, so either side can legitimately be missing.
//
// The phone field is `contact_no`. It comes from the MyTatva user service, not from our own
// schema, which is why it is snake_case among camelCase neighbours. Matches the finance
// console's NamePhoneCell (src/v2/finance/pages/shared.js) -- `phone` and `mobile` do not
// exist on this object and silently rendered a dash.
export function NamePhoneCell({ delivery }) {
    const mapping = delivery?.userOrder?.mapUserPatient;
    const person = mapping?.Patient || mapping?.User;
    return (
        <div>
            <div className="font-medium text-[var(--ops-carbon-900,#111827)]">{person?.name || "—"}</div>
            {person?.contact_no ? (
                <div className="text-xs text-gray-500">{person.contact_no}</div>
            ) : null}
        </div>
    );
}
NamePhoneCell.propTypes = { delivery: PropTypes.object };

const PARTNER_NAMES = Object.entries(DELIVERY_PARTNER_IDS_MAP).reduce((acc, [name, id]) => {
    acc[id] = name.charAt(0) + name.slice(1).toLowerCase();
    return acc;
}, {});

// Delivery Queue partner filter. Same ids and labels as the Delivery Partner column, so the
// dropdown reads exactly like the rows it filters.
export const PARTNER_OPTIONS = Object.values(DELIVERY_PARTNER_IDS_MAP).map((id) => ({
    id: String(id),
    label: PARTNER_NAMES[id],
}));

export function partnerName(delivery) {
    const id = parseInt(delivery?.deliveryPartnerId, 10);
    return PARTNER_NAMES[id] || "—";
}

// Manual deliveries through a courier vendor take an AWB + tracking link, which the vendor
// may only issue after pickup -- so both can be added after booking (pillup-api
// PUT /delivery/admin/manual/:id/tracking). In-house partners (SELF, ZYDUS) never take
// them. Mirrors isAwbPending in pillup-api classes/Delivery/Functions.js.
export const takesTrackingDetails = (delivery) =>
    isManualDelivery(delivery)
    && !NO_TRACKING_REQUIRED_PARTNER_IDS.includes(parseInt(delivery?.deliveryPartnerId, 10))
    && delivery?.status !== DELIVERY_STATUS.CANCELLED;

export const isAwbPending = (delivery) => takesTrackingDetails(delivery) && !delivery?.deliveryPartnerUniqueId;

const trackingDetailsMissing = (delivery) =>
    takesTrackingDetails(delivery) && (!delivery?.deliveryPartnerUniqueId || !getDeliveryTrackingLink(delivery));

export const isCodDelivery = (delivery) => delivery?.packageDetails?.isCod === true;
export const codAmount = (delivery) => delivery?.packageDetails?.codAmount;

/**
 * §5 — recommendation-first actions.
 *
 * Returns { primary, utilities }. Every entry names an action the LEGACY page already
 * performs against the same endpoint; the guards below are lifted from its Cell renderer
 * (src/pages/delivery/index.jsx:346-380) rather than invented. What is new is only the
 * ranking: which one is promoted to the single primary button.
 *
 * ⚙ DEFAULT (see spec §5): the ranking itself is the spec author's reading of the
 * prototype's getPrimaryAction mapped onto those guards. Flagged for product review.
 */
export function deliveryActions(delivery, { activeTab } = {}) {
    const manual = isManualDelivery(delivery);
    const trackingLink = getDeliveryTrackingLink(delivery);
    const partnerId = parseInt(delivery?.deliveryPartnerId, 10);
    const isShiprocket = partnerId === DELIVERY_PARTNER_IDS_MAP.SHIPROCKET;
    const isDtdc = partnerId === DELIVERY_PARTNER_IDS_MAP.DTDC;
    const isDelivered = delivery?.status === DELIVERY_STATUS.DELIVERED;
    const isCancelled = delivery?.status === DELIVERY_STATUS.CANCELLED;
    const isError = delivery?.status === DELIVERY_STATUS.ERROR;
    const hasManifest = Boolean(delivery?.courierDetails?.manifestUrl);
    const labelable = !manual && !isCancelled;

    const available = [];
    if (labelable) available.push({ action: "label", label: "Generate Label" });
    if (isDtdc) available.push({ action: "track", label: "Track" });
    if (trackingLink) available.push({ action: "copy", label: "Copy Link" });
    if (activeTab === "new" && !isDelivered && !isCancelled) {
        available.push({ action: "delivered", label: "Mark Delivered" });
    }
    if (isShiprocket && !manual && !isCancelled && !hasManifest) {
        available.push({ action: "manifest", label: "Generate Manifest" });
    }
    if (hasManifest) available.push({ action: "downloadManifest", label: "Download Manifest" });
    if (takesTrackingDetails(delivery)) {
        available.push({
            action: "tracking",
            label: trackingDetailsMissing(delivery) ? "Add AWB / Tracking" : "Edit AWB / Tracking",
        });
    }
    // Open-box proof photos (§11). Utilities, never the primary action: evidence to look at
    // or attach, not the next step on the row.
    //
    // View Images appears only when the delivery HAS photos -- the control's presence is
    // itself the "(if present)" signal. Add Images appears on EVERY row, because the photo
    // is optional at creation time and someone has to be able to attach it afterwards.
    if ((delivery?.deliveryBoxImages || []).length > 0) {
        available.push({ action: "viewImages", label: "View Images" });
    }
    available.push({ action: "addImages", label: "Add Images" });
    // "Meds to Pack Separately" from the linked order. Shown ONLY when there is a note, so
    // the control's presence is itself the signal — a packer scanning the queue can see at a
    // glance which boxes need special handling, without opening anything.
    if (String(delivery?.userOrder?.additionalDetails?.extraItems || '').trim()) {
        available.push({ action: "extraItems", label: "See Extra Items" });
    }
    if (!isDelivered && !isCancelled) available.push({ action: "cancel", label: "Cancel", tone: "danger" });

    const pick = (name) => available.find((entry) => entry.action === name);

    // First match wins.
    let primary = null;
    if (isError) {
        primary = pick("label") || pick("track") || { action: "review", label: "Review Issue", tone: "danger" };
    } else if (trackingDetailsMissing(delivery) && !isDelivered) {
        // A manual courier shipment still waiting on its AWB / link: getting those in is
        // the next step, and the customer's WhatsApp is held until the link arrives.
        primary = pick("tracking");
    } else if (labelable) {
        primary = pick("label");
    } else if (trackingLink && !isDelivered) {
        primary = pick("track") || pick("copy");
    } else {
        primary = pick("delivered") || null;
    }

    const utilities = available.filter((entry) => entry.action !== primary?.action);
    return { primary, utilities };
}

// Search box for Orders and Delivery Queue: a name, or a 10-digit phone number.
//
// The API treats exactly 10 digits as a phone (an exact MyTatva lookup, pillup-api
// classes/User/User.js GetBySearchTerm) and anything else as a name. A partial number
// cannot match anything -- MyTatva has no prefix search on phones -- so digits-only input
// is held back until it is exactly 10 long, then searched straight away without waiting
// for Enter. Names apply on Enter or blur, as before.
const isDigits = (value) => /^\d+$/.test(value);

export function LogisticsSearchInput({ value: applied, onSearch }) {
    const [value, setValue] = useState(applied);

    // Follow the URL when it changes underneath us (back/forward, a shared link).
    useEffect(() => setValue(applied), [applied]);

    const trimmed = value.trim();
    const partialPhone = isDigits(trimmed) && trimmed.length !== 10;

    const apply = (next) => {
        const term = next.trim();
        if (isDigits(term) && term.length !== 10) return;
        if (term !== applied) onSearch(term);
    };

    return (
        <div className="flex flex-col gap-1">
            <input
                type="text"
                value={value}
                placeholder="Search by name or phone"
                onChange={(event) => {
                    const next = event.target.value;
                    setValue(next);
                    if (/^\d{10}$/.test(next.trim())) apply(next);
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter") apply(value);
                }}
                onBlur={() => apply(value)}
                className="min-w-[240px] rounded-lg border border-[var(--ops-carbon-200)] px-3 py-2 font-sans text-sm"
            />
            {partialPhone && (
                <span className="text-xs text-amber-700">Enter all 10 digits to search by phone</span>
            )}
        </div>
    );
}
LogisticsSearchInput.propTypes = {
    value: PropTypes.string.isRequired,
    onSearch: PropTypes.func.isRequired,
};
